<script setup>
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'
import AppLayout from '@/Layouts/AppLayout.vue'
import PageShell from '@/Components/UI/PageShell.vue'
import UiButton from '@/Components/UI/UiButton.vue'

defineOptions({ layout: AppLayout })
const { t } = useI18n()

const props = defineProps({
  channels:  { type: Array, default: () => [] },
  types:     { type: Array, default: () => [] },
  platforms: { type: Array, default: () => [] }
})

// Each row is either persisted (id present) or draft (id === null, server
// roundtrip on save). Local state — never written until the user clicks
// Save on the row.
const rows = ref(props.channels.map(c => ({ ...c, _dirty: false, _saving: false, _error: null })))

const csrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content || ''

function blankRow() {
  return {
    id: null,
    channel_type: 'social_page',
    platform: 'instagram',
    url: '',
    label: '',
    position: rows.value.length,
    _dirty: true,
    _saving: false,
    _error: null
  }
}

function addRow() { rows.value.push(blankRow()) }

function markDirty(row) { row._dirty = true; row._error = null }

async function saveRow(row) {
  if (row._saving) return
  if (!row.url || !row.url.trim()) { row._error = t('public_channels.errors.url_required'); return }
  row._saving = true
  row._error = null
  try {
    const isCreate = !row.id
    const url = isCreate
      ? '/admin/public_channels'
      : `/admin/public_channels/${row.id}`
    const res = await fetch(url, {
      method: isCreate ? 'POST' : 'PATCH',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-CSRF-Token': csrfToken(),
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify({
        public_channel: {
          channel_type: row.channel_type,
          platform:     row.platform,
          url:          row.url.trim(),
          label:        (row.label || '').trim()
        }
      })
    })
    const body = await res.json().catch(() => ({}))
    if (!res.ok) {
      row._error = (body.errors || [`HTTP ${res.status}`]).join(', ')
      return
    }
    Object.assign(row, body.channel, { _dirty: false, _error: null })
  } catch (e) {
    row._error = e.message || 'Network error'
  } finally {
    row._saving = false
  }
}

async function deleteRow(row) {
  if (!confirm(t('public_channels.confirmDelete'))) return
  if (!row.id) {
    rows.value = rows.value.filter(r => r !== row)
    return
  }
  const res = await fetch(`/admin/public_channels/${row.id}`, {
    method: 'DELETE',
    credentials: 'same-origin',
    headers: { 'X-CSRF-Token': csrfToken(), 'X-Requested-With': 'XMLHttpRequest' }
  })
  if (res.ok) rows.value = rows.value.filter(r => r !== row)
}

function typeLabel(t_)  { return t(`public_channels.types.${t_}`) }
function platLabel(p)   { return p.charAt(0).toUpperCase() + p.slice(1) }
function previewUrl(row){ return row.url || '' }

// Runtime URL expansion hint, shown below the preview link. Some rows
// don't render their stored URL verbatim — they get a query param
// appended per-page (e.g. Telegram bot ?start=vehicle_42 from a catalog
// CTA, WhatsApp ?text=... pre-typed inquiry). This makes that explicit
// in the constructor so the operator isn't guessing.
function runtimeHint(row) {
  if (!row.url || row._dirty) return ''

  if (row.channel_type === 'message' && row.platform === 'telegram' && /t\.me\//.test(row.url)) {
    const sep = row.url.includes('?') ? '&' : '?'
    return t('public_channels.hints.telegram_start', { example: `${row.url}${sep}start=vehicle_<id>` })
  }

  if (row.channel_type === 'message' && row.platform === 'whatsapp') {
    const sep = row.url.includes('?') ? '&' : '?'
    return t('public_channels.hints.whatsapp_text', { example: `${row.url}${sep}text=...` })
  }

  if (row.channel_type === 'website') {
    return t('public_channels.hints.website_brand')
  }

  return ''
}
</script>

<template>
  <PageShell :title="t('public_channels.title')" back-href="/settings">
    <template #subtitle>
      <p class="text-sm text-[color:var(--inkSoft)]">{{ t('public_channels.subtitle') }}</p>
    </template>

    <div class="space-y-3">
      <div
        v-for="row in rows"
        :key="row.id ?? `draft-${row.position}`"
        class="row"
      >
        <div class="row-grid">
          <div>
            <label class="row-label">{{ t('public_channels.fields.type') }}</label>
            <select
              v-model="row.channel_type"
              class="row-input"
              @change="markDirty(row)"
            >
              <option v-for="t_ in types" :key="t_" :value="t_">{{ typeLabel(t_) }}</option>
            </select>
          </div>

          <div>
            <label class="row-label">{{ t('public_channels.fields.platform') }}</label>
            <select
              v-model="row.platform"
              class="row-input"
              @change="markDirty(row)"
            >
              <option v-for="p in platforms" :key="p" :value="p">{{ platLabel(p) }}</option>
            </select>
          </div>

          <div class="row-grow">
            <label class="row-label">{{ t('public_channels.fields.url') }}</label>
            <input
              v-model="row.url"
              type="text"
              :placeholder="t('public_channels.fields.urlPlaceholder')"
              class="row-input"
              @input="markDirty(row)"
            />
          </div>

          <div>
            <label class="row-label">{{ t('public_channels.fields.label') }}</label>
            <input
              v-model="row.label"
              type="text"
              :placeholder="t('public_channels.fields.labelPlaceholder')"
              class="row-input"
              @input="markDirty(row)"
            />
          </div>

          <div class="row-actions">
            <UiButton
              size="sm"
              variant="primary"
              :disabled="!row._dirty || row._saving"
              @click="saveRow(row)"
            >
              {{ row._saving ? t('common.saving') : (row.id ? t('common.save') : t('common.create')) }}
            </UiButton>
            <UiButton
              size="sm"
              variant="ghost"
              @click="deleteRow(row)"
            >
              {{ t('common.delete') }}
            </UiButton>
          </div>
        </div>

        <p v-if="row._error" class="row-error">{{ row._error }}</p>
        <p v-if="previewUrl(row) && !row._dirty" class="row-preview">
          <a :href="previewUrl(row)" target="_blank" rel="noopener">{{ previewUrl(row) }} ↗</a>
        </p>
        <p v-if="runtimeHint(row)" class="row-runtime-hint">
          {{ runtimeHint(row) }}
        </p>
      </div>

      <p v-if="rows.length === 0" class="empty">
        {{ t('public_channels.empty') }}
      </p>

      <UiButton variant="secondary" icon-left="plus" @click="addRow">
        {{ t('public_channels.addChannel') }}
      </UiButton>
    </div>
  </PageShell>
</template>

<style scoped>
.row {
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius-md, 8px);
  padding: 0.85rem 1rem;
}
.row-grid {
  display: grid;
  gap: 0.6rem 0.75rem;
  grid-template-columns: 1fr;
  align-items: end;
}
@media (min-width: 768px) {
  .row-grid {
    grid-template-columns: 9rem 9rem 1fr 9rem auto;
  }
}
.row-label {
  display: block;
  font-size: 0.6875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: var(--muted);
  margin-bottom: 0.25rem;
}
.row-input {
  width: 100%;
  padding: 0.4rem 0.6rem;
  background: var(--surface);
  border: 1px solid var(--line);
  border-radius: var(--radius-input, 6px);
  color: var(--ink);
  font-size: 0.875rem;
}
.row-input:focus { outline: 2px solid var(--accent); outline-offset: 1px; }
.row-grow { min-width: 0; }
.row-actions { display: flex; gap: 0.4rem; }
.row-error {
  margin: 0.4rem 0 0;
  color: var(--danger);
  font-size: 0.8125rem;
}
.row-preview {
  margin: 0.35rem 0 0;
  font-size: 0.75rem;
  color: var(--muted);
}
.row-preview a { color: var(--accent); text-decoration: none; }
.row-preview a:hover { text-decoration: underline; }
.row-runtime-hint {
  margin: 0.15rem 0 0;
  font-size: 0.6875rem;
  color: var(--muted);
  font-style: italic;
}
.empty {
  padding: 1.5rem;
  text-align: center;
  color: var(--muted);
  background: var(--surface);
  border: 1px dashed var(--line);
  border-radius: var(--radius-md, 8px);
}
</style>
